import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useState,
} from 'react';

import { useSentryLogger } from '@App/hooks/log';
import { Offer, SelectedOffers } from '@App/interfaces/offer';
import { RecommendedOffer } from '@App/interfaces/offer';
import { getOffers, getRecommendedOffers } from '@App/requests/getOfferData';
import { savePreferableOffer } from '@App/requests/questionnaires';
import { saveEventBooking, saveOffer } from '@App/requests/saveApplicationData';

import { EventBooking } from '../interfaces/eventBooking';

import { ApplicationContext } from './Application';

export const OfferContext = createContext<any>(null);

interface OfferContextProviderProps {
    children?: ReactNode;
    getAccessToken: Function;
    getIdToken: Function;
}

const OfferContextProvider = ({
    children,
    getAccessToken,
    getIdToken,
}: OfferContextProviderProps) => {
    const { applicationId, offers, recommendedOffers, setRecommendedOffers } =
        useContext(ApplicationContext);

    const [loading, setLoading] = useState(false);

    const [offerData, setOfferData] = useState<SelectedOffers>();

    const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);

    const { handleError } = useSentryLogger(applicationId, 'Offer context');

    const fetchOffers = useCallback(async () => {
        try {
            setLoading(true);
            if (offers.length === 0) {
                return;
            }
            const result = await getOffers(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
            );
            setOfferData(result);

            const customerSelectedOffer =
                offers.find(
                    (offer: Offer) =>
                        offer.productCode === result.selectedProductCode,
                ) || null;
            setSelectedOffer(customerSelectedOffer);
        } catch (e) {
            return handleError(e, 'fetching offers');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, offers]);

    const fetchRecommendedOffers = useCallback(async () => {
        try {
            setLoading(true);

            if (offers.length === 0) {
                return;
            }

            const result = recommendedOffers
                ? recommendedOffers
                : await getRecommendedOffers(
                      applicationId,
                      await getAccessToken(),
                      await getIdToken(),
                  );

            setRecommendedOffers(result);

            const selectedProductCode = result.recommendedOffers.find(
                (it: RecommendedOffer) => it.selected,
            )?.code;

            const customerSelectedOffer =
                offers.find(
                    (offer: Offer) => offer.productCode === selectedProductCode,
                ) || null;

            setSelectedOffer(customerSelectedOffer);
        } catch (e) {
            return handleError(e, 'fetching recommended offers');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId, offers]);

    const saveSelectedOffer = async () => {
        try {
            setLoading(true);
            const response = await saveOffer(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
                selectedOffer,
            );
            return response;
        } catch (e) {
            return handleError(e, 'save selected offer');
        } finally {
            setLoading(false);
        }
    };

    const saveScheduledEventBooking = async (eventBooking: EventBooking) => {
        try {
            setLoading(true);

            const response = await saveEventBooking(
                await getAccessToken(),
                applicationId,
                eventBooking,
                await getIdToken(),
            );
            return response;
        } catch (e) {
            return handleError(e, 'save call date and time');
        } finally {
            setLoading(false);
        }
    };

    const selectPreferableOffer = async (
        applicationId: string,
        productCode: string,
    ) => {
        try {
            setLoading(true);
            const response = await savePreferableOffer(
                applicationId,
                productCode,
            );

            setRecommendedOffers(response);

            return response;
        } catch (e) {
            return handleError(e, 'save preferable offer answers');
        } finally {
            setLoading(false);
        }
    };

    return (
        <OfferContext.Provider
            value={{
                loading,
                selectedOffer,
                setSelectedOffer,
                saveSelectedOffer,
                saveScheduledEventBooking,
                selectPreferableOffer,
                offerData,
                fetchOffers,
                fetchRecommendedOffers,
            }}
        >
            {children}
        </OfferContext.Provider>
    );
};

export default OfferContextProvider;
